import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Footer from '../Footer';
import CalendarViewControls from '../calendar/CalendarViewControls';
import CalendarWeekView from '../calendar/CalendarWeekView';
import CalendarListView from '../calendar/CalendarListView';
import CalendarMonthView from '../calendar/CalendarMonthView';
import {
  FORTNIGHT_DAYS,
  addDays,
  fetchEventsForDateRange,
  fetchEventsForMonthView,
  formatPeriodLabel,
  getMonday,
} from '../../utils/calendarHelpers';
import './styles/CalendarPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const CalendarPage = () => {
  const [viewMode, setViewMode] = useState('week');
  const [rangeStart, setRangeStart] = useState(() => getMonday(new Date()));
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventsByDate, setEventsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const rangeEnd = useMemo(
    () => addDays(rangeStart, FORTNIGHT_DAYS - 1),
    [rangeStart]
  );

  const periodLabel = useMemo(
    () => formatPeriodLabel(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let data;
      if (viewMode === 'calendar') {
        data = await fetchEventsForMonthView(API_BASE_URL, currentDate);
      } else {
        data = await fetchEventsForDateRange(API_BASE_URL, rangeStart, FORTNIGHT_DAYS);
      }
      setEventsByDate(data);
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Nem sikerült betölteni az eseményeket');
      setEventsByDate({});
    } finally {
      setLoading(false);
    }
  }, [viewMode, rangeStart, currentDate]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    setSelectedDate(null);
  }, [currentDate, viewMode]);

  const handlePrevPeriod = () => {
    if (viewMode === 'calendar') {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      );
    } else {
      setRangeStart((prev) => addDays(prev, -FORTNIGHT_DAYS));
    }
  };

  const handleNextPeriod = () => {
    if (viewMode === 'calendar') {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
      );
    } else {
      setRangeStart((prev) => addDays(prev, FORTNIGHT_DAYS));
    }
  };

  const handleNavigateMonth = (direction) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1)
    );
  };

  return (
    <div className="calendar-page">
      <section id="calendar" className="calendar-section">
        <div className="container calendar-page-container">
          <div className="calendar-header">
            <h2 className="section-title">Programok Naptára</h2>
            <p className="section-subtitle">
              Tekintse meg a heti programokat és eseményeket egy helyen.
            </p>
          </div>

          <div className="calendar-container">
            <CalendarViewControls
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              periodLabel={periodLabel}
              onPrevPeriod={handlePrevPeriod}
              onNextPeriod={handleNextPeriod}
              showPeriodNav={viewMode !== 'calendar'}
            />

            {error && (
              <div className="error-message calendar-error">
                ⚠️ {error}
                <button type="button" onClick={loadEvents} className="retry-btn">
                  Újrapróbálás
                </button>
              </div>
            )}

            {loading ? (
              <p className="calendar-loading">Betöltés…</p>
            ) : (
              <>
                {viewMode === 'week' && (
                  <CalendarWeekView rangeStart={rangeStart} eventsByDate={eventsByDate} />
                )}
                {viewMode === 'list' && (
                  <CalendarListView rangeStart={rangeStart} eventsByDate={eventsByDate} />
                )}
                {viewMode === 'calendar' && (
                  <CalendarMonthView
                    currentDate={currentDate}
                    eventsByDate={eventsByDate}
                    selectedDate={selectedDate}
                    onSelectDate={setSelectedDate}
                    onNavigateMonth={handleNavigateMonth}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CalendarPage;
